import { useCallback, useEffect, useRef, useState } from 'react';
import { describeError } from '../../services/errorMessage';
import {
    TalentpoolFehler,
    bearbeiteTalent,
    ladeTalentpool,
    loescheTalent,
    type LoeschErgebnis,
} from '../../services/talentpool/talentpoolService';
import { ersetzeZeile, neuAnzahl, ohneZeile } from '../../services/talentpool/talentpoolLogik';
import type { TalentStatus, TalentpoolZeile } from '../../services/talentpool/talentpoolTypes';

/**
 * Daten und Schreibwege des Talentpools (Muster `useMaterial`).
 *
 * Geladen wird beim Öffnen und auf Knopfdruck — kein Realtime: neue
 * Bewerbungen kommen ein paar Mal am Tag, das Abzeichen im Menü zählt sie
 * ohnehin im Takt nach. Die Antwort einer RPC wird sofort in die Liste
 * übernommen; nach jedem Stand meldet der Hook die Zahl der neuen ans Menü
 * (`onNeuGeaendert`).
 *
 * Kein setState im Effektrumpf (react-hooks/set-state-in-effect): „lädt“
 * ergibt sich daraus, dass die letzte Antwort zu einer älteren Ladenummer
 * gehört (Muster AdminApiLogsView). Eine überholte Antwort wird verworfen.
 *
 * Ein Ladefehler behält den letzten guten Stand und legt den Klartext
 * daneben. Die Schreibwege werfen `TalentpoolFehler` — der Aufrufer zeigt
 * ihn dort, wo geklickt wurde.
 */

interface Antwort {
    /** Zu welcher Ladenummer die Antwort gehört. */
    nr: number;
    zeilen: TalentpoolZeile[] | null;
    gekappt: boolean;
    nichtEingerichtet: string;
    fehler: string;
    /** Zeitpunkt des letzten geglückten Ladens (ms). */
    standMs: number;
}

export interface TalentpoolZustand {
    zeilen: TalentpoolZeile[] | null;
    /** Es gibt mehr Bewerbungen, als geladen wurden. */
    gekappt: boolean;
    /** Klartext, wenn SQL Nr. 188/189 in dieser Umgebung fehlt, sonst ''. */
    nichtEingerichtet: string;
    laedt: boolean;
    /** Klartext des letzten Ladefehlers, sonst ''. */
    ladeFehler: string;
    standMs: number;
    neuLaden: () => void;
    speichern: (id: string, status: TalentStatus, notiz: string) => Promise<TalentpoolZeile>;
    loeschen: (id: string) => Promise<LoeschErgebnis>;
}

export const useTalentpool = (onNeuGeaendert?: (anzahl: number) => void): TalentpoolZustand => {
    const [neuladen, setNeuladen] = useState(0);
    const [antwort, setAntwort] = useState<Antwort | null>(null);

    const onNeuRef = useRef(onNeuGeaendert);
    useEffect(() => { onNeuRef.current = onNeuGeaendert; }, [onNeuGeaendert]);

    useEffect(() => {
        let abgebrochen = false;
        const nr = neuladen;
        void ladeTalentpool().then(
            (stand) => {
                if (abgebrochen) return;
                setAntwort({ nr, zeilen: stand.zeilen, gekappt: stand.gekappt, nichtEingerichtet: '', fehler: '', standMs: Date.now() });
            },
            (e: unknown) => {
                if (abgebrochen) return;
                if (e instanceof TalentpoolFehler && e.code === 'nicht_eingerichtet') {
                    setAntwort({ nr, zeilen: null, gekappt: false, nichtEingerichtet: e.message, fehler: '', standMs: Date.now() });
                    return;
                }
                setAntwort(alt => ({
                    nr,
                    zeilen: alt?.zeilen ?? null,
                    gekappt: alt?.gekappt ?? false,
                    nichtEingerichtet: '',
                    fehler: describeError(e),
                    standMs: alt?.standMs ?? 0,
                }));
            },
        );
        return () => { abgebrochen = true; };
    }, [neuladen]);

    // Jeder neue Stand (geladen, gespeichert, gelöscht) meldet die Zahl der
    // neuen ans Menü — an EINER Stelle, damit Abzeichen und Liste nie
    // auseinanderlaufen.
    const zeilen = antwort?.zeilen ?? null;
    useEffect(() => {
        if (zeilen) onNeuRef.current?.(neuAnzahl(zeilen));
    }, [zeilen]);

    const neuLaden = useCallback(() => setNeuladen(n => n + 1), []);

    const lokal = useCallback((aendern: (z: TalentpoolZeile[]) => TalentpoolZeile[]) => {
        setAntwort(alt => (alt?.zeilen ? { ...alt, zeilen: aendern(alt.zeilen) } : alt));
    }, []);

    const speichern = useCallback(async (id: string, status: TalentStatus, notiz: string) => {
        const zeile = await bearbeiteTalent(id, status, notiz);
        lokal(z => ersetzeZeile(z, zeile));
        return zeile;
    }, [lokal]);

    const loeschen = useCallback(async (id: string) => {
        const ergebnis = await loescheTalent(id);
        lokal(z => ohneZeile(z, id));
        return ergebnis;
    }, [lokal]);

    return {
        zeilen,
        gekappt: antwort?.gekappt ?? false,
        nichtEingerichtet: antwort?.nichtEingerichtet ?? '',
        laedt: antwort?.nr !== neuladen,
        ladeFehler: antwort?.fehler ?? '',
        standMs: antwort?.standMs ?? 0,
        neuLaden,
        speichern,
        loeschen,
    };
};
